<template>
  <div class="aggregation-selector">

    <!-- Détection automatique du type de champ -->
    <!-- <div v-if="detectedFieldType" class="bg-gray-50 p-3 rounded-lg mb-4">
      <div class="flex items-center gap-2">
        <span class="text-gray-600">🔍</span>
        <span class="text-sm text-gray-700">
          Champ détecté : <strong>{{ getFieldTypeLabel(detectedFieldType) }}</strong>
        </span>
      </div>
    </div> -->

    <!-- Suggestions intelligentes -->
    <!-- <div v-if="smartSuggestions.length > 0" class="bg-green-50 p-3 rounded-lg mb-4">
      <div class="flex items-center gap-2 mb-2">
        <span class="text-green-600">💡</span>
        <span class="text-sm font-medium text-green-700">
          Suggestions pour "{{ fieldName }}"
        </span>
      </div>
      <div class="flex flex-wrap gap-2">
        <button v-for="suggestion in smartSuggestions" :key="suggestion.type"
          @click="selectAggregation(suggestion.type)"
          :class="[
            'text-xs px-3 py-1.5 rounded-full transition-all touch-manipulation',
            selectedAggregation === suggestion.type
              ? 'bg-green-200 text-green-800 ring-2 ring-green-300'
              : 'bg-green-100 text-green-800 hover:bg-green-200 active:scale-95'
          ]">
          {{ suggestion.label }}
        </button>
      </div>
    </div> -->

    <!-- Sélecteur compact avec dropdown ShadCN -->
    <div>
      <label class="block text-xs text-gray-600 mb-2">Fonction d'agrégation</label>
      <Select :model-value="selectedAggregation" @update:model-value="(value) => selectAggregation(value as AggregationType)">
        <SelectTrigger class="w-full">
          <SelectValue :placeholder="$t('customcharts.placeholders.choose_aggregation')">
            <span v-if="selectedAggregationConfig" class="flex items-center gap-2">
              <span>{{ selectedAggregationConfig.label }}</span>
              <span v-if="isAdvancedFunction(selectedAggregation)" 
                class="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">
                Avancé
              </span>
            </span>
          </SelectValue>
        </SelectTrigger>
        <SelectContent class="max-h-80">
          <!-- Agrégations de base -->
          <div v-if="basicAggregations.length > 0">
            <SelectGroup>
              <SelectLabel class="px-2 py-1.5 text-xs font-medium text-gray-500">
                📊 Analyses de base
              </SelectLabel>
              <SelectItem v-for="agg in basicAggregations" :key="agg.type" :value="agg.type">
                <div class="flex-1 min-w-0">
                  <div class="font-medium text-sm">{{ agg.label }}</div>
                  <div class="text-xs text-gray-500 truncate">{{ agg.description }}</div>
                </div>
              </SelectItem>
            </SelectGroup>
          </div>

          <!-- Agrégations statistiques -->
          <div v-if="statisticalAggregations.length > 0">
            <SelectGroup>
              <SelectLabel class="px-2 py-1.5 text-xs font-medium text-gray-500">
                📈 Analyses statistiques
              </SelectLabel>
              <SelectItem v-for="agg in statisticalAggregations" :key="agg.type" :value="agg.type">
                <div class="flex-1 min-w-0">
                  <div class="font-medium text-sm flex items-center gap-2">
                    {{ agg.label }}
                    <span class="text-xs bg-purple-100 text-purple-700 px-1.5 py-0.5 rounded">Avancé</span>
                  </div>
                  <div class="text-xs text-gray-500 truncate">{{ agg.description }}</div>
                </div>
              </SelectItem>
            </SelectGroup>
          </div>

          <!-- Agrégations temporelles -->
          <div v-if="temporalAggregations.length > 0">
            <SelectGroup>
              <SelectLabel class="px-2 py-1.5 text-xs font-medium text-gray-500">
                ⏱️ Analyses temporelles
              </SelectLabel>
              <SelectItem v-for="agg in temporalAggregations" :key="agg.type" :value="agg.type">
                <div class="flex-1 min-w-0">
                  <div class="font-medium text-sm">{{ agg.label }}</div>
                  <div class="text-xs text-gray-500 truncate">{{ agg.description }}</div>
                </div>
              </SelectItem>
            </SelectGroup>
          </div>
        </SelectContent>
      </Select>

      <!-- Info contextuelle (uniquement en mode non-compact) -->
      <div v-if="selectedAggregationConfig && !compactMode && selectedAggregationConfig.useCases.length > 0" 
           class="mt-2 p-2 bg-blue-50 rounded text-xs text-blue-700">
        <span class="font-medium">💡 Utile pour :</span> {{ selectedAggregationConfig.useCases.slice(0, 2).join(', ') }}
      </div>
    </div>

    <!-- Aide contextuelle en mode compact -->
    <!-- <div v-if="compactMode && selectedAggregationConfig" 
      class="mt-4 p-3 bg-blue-50 rounded-lg">
      <div class="flex items-start gap-2">
        <span class="text-blue-600 text-sm">ℹ️</span>
        <div class="text-sm text-blue-700">
          <div class="font-medium mb-1">{{ selectedAggregationConfig.label }}</div>
          <div class="text-xs">{{ selectedAggregationConfig.description }}</div>
        </div>
      </div>
    </div> -->

    <!-- Mode bottom sheet pour mobile -->
    <Teleport to="body" v-if="showBottomSheet">
      <div class="fixed inset-0 z-50 flex items-end">
        <!-- Overlay -->
        <div class="absolute inset-0 bg-black bg-opacity-50" 
          @click="closeBottomSheet"></div>
        
        <!-- Bottom sheet -->
        <div class="relative w-full bg-background rounded-t-lg max-h-[80vh] overflow-y-auto">
          <div class="p-4">
            <!-- Handle -->
            <div class="w-12 h-1 bg-border rounded-full mx-auto mb-4"></div>
            
            <!-- Contenu -->
            <div class="aggregation-selector-content">
              <slot name="bottom-sheet-content">
                <!-- Copie du contenu principal adaptée -->
                <h4 class="text-lg font-semibold text-foreground mb-4">
                  Choisir une fonction d'agrégation
                </h4>
                <!-- ... reste du contenu ... -->
              </slot>
            </div>
          </div>
        </div>
      </div>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
// import { useI18n } from 'vue-i18n'
import { AGGREGATION_TYPES, getSmartAggregationSuggestions, type AggregationType, type AggregationConfig } from '../../types/customCharts'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue, SelectGroup, SelectLabel } from '../ui/select'

interface Props {
  modelValue: AggregationType
  fieldName?: string
  fieldType?: string
  compactMode?: boolean
  bottomSheetMode?: boolean
  showAdvanced?: boolean
}

interface Emits {
  (e: 'update:modelValue', value: AggregationType): void
  (e: 'change', config: AggregationConfig): void
}

const props = withDefaults(defineProps<Props>(), {
  fieldName: '',
  fieldType: 'number',
  compactMode: false,
  bottomSheetMode: false,
  showAdvanced: true
})

const emit = defineEmits<Emits>()
// const { t } = useI18n()

// État local
const selectedAggregation = ref<AggregationType>(props.modelValue)
const showBottomSheet = ref(false)

// Type de champ détecté
// const detectedFieldType = computed(() => {
//   if (!props.fieldName || !props.fieldType) return null
//   return props.fieldType
// })

// Suggestions intelligentes
const smartSuggestions = computed(() => {
  if (!props.fieldName || !props.fieldType) return []
  return getSmartAggregationSuggestions(props.fieldType, props.fieldName)
})

// Agrégations disponibles (filtrées selon les préférences)
const availableAggregations = computed(() => {
  let aggregations = AGGREGATION_TYPES

  // Filtrer selon compatibilité avec le type de champ
  if (props.fieldType && props.fieldType !== 'any') {
    aggregations = aggregations.filter(agg => 
      agg.compatibleFields.includes(props.fieldType!) || 
      agg.compatibleFields.includes('any')
    )
  }

  // Masquer les fonctions avancées si demandé
  if (!props.showAdvanced) {
    aggregations = aggregations.filter(agg => !isAdvancedFunction(agg.type))
  }

  // En mode compact, prioriser les suggestions intelligentes
  if (props.compactMode) {
    const suggested = smartSuggestions.value
    const basicTypes: AggregationType[] = ['sum', 'count', 'average', 'max', 'min']
    const basic = aggregations.filter(agg => basicTypes.includes(agg.type))
    
    // Prioriser les suggestions intelligentes, puis ajouter les basiques manquantes
    const combined = [...suggested, ...basic.filter(b => !suggested.some(s => s.type === b.type))]
    return combined.slice(0, 8) // Augmenter à 8 options max en compact
  }

  return aggregations
})

// Catégoriser les agrégations pour un affichage organisé
const basicAggregations = computed(() => {
  const basicTypes: AggregationType[] = ['sum', 'count', 'average', 'max', 'min']
  return availableAggregations.value.filter(agg => basicTypes.includes(agg.type))
})

const statisticalAggregations = computed(() => {
  const statTypes: AggregationType[] = ['median', 'std_dev', 'variance', 'percentile_25', 'percentile_75', 'percentile_90', 'percentile_95']
  return availableAggregations.value.filter(agg => statTypes.includes(agg.type))
})

const temporalAggregations = computed(() => {
  const temporalTypes: AggregationType[] = ['first', 'last']
  return availableAggregations.value.filter(agg => temporalTypes.includes(agg.type))
})

// Configuration de l'agrégation sélectionnée
const selectedAggregationConfig = computed(() => {
  return AGGREGATION_TYPES.find(agg => agg.type === selectedAggregation.value)
})

// Fonctions utilitaires
const isAdvancedFunction = (type: AggregationType): boolean => {
  return ['std_dev', 'variance', 'median', 'percentile_25', 'percentile_75', 'percentile_90', 'percentile_95'].includes(type)
}

// const getFieldTypeLabel = (type: string): string => {
//   const labels: Record<string, string> = {
//     'currency': t('aggregation.types.currency'),
//     'number': t('aggregation.types.number'),
//     'integer': t('aggregation.types.integer'),
//     'duration': t('aggregation.types.duration'),
//     'date': t('aggregation.types.date')
//   }
//   return labels[type] || type
// }

// Méthodes
const selectAggregation = (type: AggregationType) => {
  selectedAggregation.value = type
  emit('update:modelValue', type)
  
  const config = AGGREGATION_TYPES.find(agg => agg.type === type)
  if (config) {
    emit('change', config)
  }

  if (showBottomSheet.value) {
    closeBottomSheet()
  }
}

const openBottomSheet = () => {
  if (props.bottomSheetMode) {
    showBottomSheet.value = true
  }
}

const closeBottomSheet = () => {
  showBottomSheet.value = false
}

// Watchers
watch(() => props.modelValue, (newValue) => {
  selectedAggregation.value = newValue
})

// Exposition des méthodes pour usage externe
defineExpose({
  openBottomSheet,
  closeBottomSheet
})
</script>

<style scoped>
@reference "../../style.css";

/* Optimisations tactiles */
.touch-manipulation {
  touch-action: manipulation;
  -webkit-touch-callout: none;
  -webkit-user-select: none;
  user-select: none;
}

/* États actifs pour mobile */
@media (hover: none) {
  .hover\:shadow-md:hover {
    box-shadow: none;
  }
  
  .active\:scale-95:active,
  .active\:scale-98:active {
    transition: transform 0.1s ease-in-out;
  }
  
  .active\:scale-95:active {
    transform: scale(0.95);
  }
  
  .active\:scale-98:active {
    transform: scale(0.98);
  }
}

/* Animation du bottom sheet */
.fixed.inset-0 {
  animation: fadeIn 0.2s ease-out;
}

.bg-background.rounded-t-lg {
  animation: slideUp 0.3s ease-out;
}

@keyframes fadeIn {
  from { opacity: 0; }
  to { opacity: 1; }
}

@keyframes slideUp {
  from { transform: translateY(100%); }
  to { transform: translateY(0); }
}

/* Mode compact mobile */
@media (max-width: 640px) {
  .compactMode .space-y-3 > :not([hidden]) ~ :not([hidden]) {
    margin-top: 0.5rem;
  }
  
  .compactMode .p-4 {
    padding: 0.75rem;
  }
}

/* Scroll smooth pour bottom sheet */
.max-h-\[80vh\] {
  scroll-behavior: smooth;
  -webkit-overflow-scrolling: touch;
}

/* Handle du bottom sheet */
.w-12.h-1 {
  background: rgba(0, 0, 0, 0.2);
  border-radius: 2px;
}
</style>